import type { ErrorCode } from '@detachly/contracts'
import { ApiRequestError } from './api'

/**
 * The copy table from web-routes-and-states.md. Every message names the next action, because an
 * error the user cannot act on is just noise. Raw server text is never rendered on its own.
 */
type ErrorCopy = { title: string; action: string }

const COPY: Partial<Record<ErrorCode, ErrorCopy>> = {
  UNAUTHORIZED: {
    title: 'Your session expired.',
    action: 'Sign in again to continue.',
  },
  NOT_FOUND: {
    title: 'We could not find that.',
    action: 'It may have been deleted. Go back to your projects.',
  },
  VALIDATION: {
    title: 'Some details need fixing.',
    action: 'Check the highlighted fields and try again.',
  },
  FRAMER_AUTH: {
    title: 'Framer rejected this API key.',
    action: 'Create a new key in Framer site settings, then reconnect.',
  },
  FRAMER_PROJECT: {
    title: 'That Framer project is not reachable with this key.',
    action: 'Check the project URL, and that the key belongs to the same project.',
  },
  FRAMER_RATE: {
    title: 'Framer is rate limiting us.',
    action: 'Wait a minute and run it again.',
  },
  FRAMER_PARTIAL: {
    title: 'Framer answered only part of the project.',
    action: 'The result is incomplete — run the scan again.',
  },
  PACKAGE_INSUFFICIENT: {
    title: 'This project has no AI page left.',
    action: 'Content Pack and Scaffold stay free. Add a package to rebuild pages with AI.',
  },
  JOB_CONFLICT: {
    title: 'A job of this kind is already running.',
    action: 'Wait for it to finish, then start the next one.',
  },
  PRECONDITION_FAILED: {
    title: 'This needs a successful scan first.',
    action: 'Run a scan, then come back.',
  },
  BUILD_FAILED: {
    title: 'The generated project did not compile.',
    action: 'Open the report to see which step failed.',
  },
  TIMEOUT: {
    title: 'The job ran out of time.',
    action: 'Start it again — nothing was charged.',
  },
  RATE_LIMITED: {
    title: 'Too many requests.',
    action: 'Give it a moment and try again.',
  },
  INTERNAL: {
    title: 'Something broke on our side.',
    action: 'Try again. If it keeps happening, contact support.',
  },
}

const FALLBACK: ErrorCopy = COPY.INTERNAL ?? {
  title: 'Something went wrong.',
  action: 'Try again.',
}

export function copyForCode(code: ErrorCode): ErrorCopy {
  return COPY[code] ?? FALLBACK
}

/** Turns anything a screen may catch into copy it can render. */
export function describeError(error: unknown): ErrorCopy & { code: ErrorCode } {
  if (error instanceof ApiRequestError) {
    const copy = copyForCode(error.code)
    // The server's own message is more specific for validation; the table stays the fallback.
    return error.code === 'VALIDATION' && error.message
      ? { code: error.code, title: copy.title, action: error.message }
      : { code: error.code, ...copy }
  }
  return { code: 'INTERNAL', ...FALLBACK }
}

/** Field-level messages from a `VALIDATION` envelope, keyed by field name. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError) || error.code !== 'VALIDATION') return {}

  const details = error.details
  if (!Array.isArray(details)) return {}

  const entries: Record<string, string> = {}
  for (const issue of details) {
    if (
      issue &&
      typeof issue === 'object' &&
      'path' in issue &&
      'message' in issue &&
      typeof issue.path === 'string' &&
      typeof issue.message === 'string'
    ) {
      entries[issue.path] ??= issue.message
    }
  }
  return entries
}
